"use client";

import {yupResolver} from "@hookform/resolvers/yup";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

import {NeoButton, NeoDatePicker, NeoTextBox} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {NeoHeader} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {FleetService} from "@/service/fleet.service";

export type FormMiscellaneous = {
  vehicleId: string;
  id?: string;
  date?: Date | null;
  totalAmount: number;
  expenseType: number;
  description?: string | null;
};

type MiscellaneousFormProps = {
  data: any;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
};

export const ExpenseMiscellaneousForm: React.FC<MiscellaneousFormProps> = ({
  data,
  onSubmit
}) => {
  const maxDate = useMemo(() => new Date(), []);
  const {filterContext} = useNeoContext();
  const [loading, setLoading] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [expensetypes, setExpenseTypes] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [defaultformdata, setdefaultformdata] = useState<any>({});
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const toNumOrUndef = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
      const s = v.trim().replace(/,/g, "");
      if (!s) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const schema = yup.object().shape({
    vehicleId: yup.string().required("Please select vehicle"),
    id: yup.string().optional(),
    date: yup
      .date()
      .transform((v, o) => {
        if (o === "" || o == null) return null;
        const d = new Date(o);
        return isNaN(d.getTime()) ? null : d;
      })
      .nullable()
      .typeError("Enter a valid date")
      .max(today, "Date cannot be in the future"),
    expenseType: yup.number().nullable().required("Please select expense type"),
    totalAmount: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Total cost must be a number")
      .moreThan(0, "Total cost must be greater than 0")
      .required("Please enter total cost"),
    description: yup.string().nullable().default("")
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: {isSubmitting, errors}
  } = useForm<FormMiscellaneous>({
    mode: "onChange",
    resolver: yupResolver<FormMiscellaneous>(schema),
    shouldUnregister: true
  });

  const mapApiToForm = (apiData: any): FormMiscellaneous => {
    return {
      vehicleId: apiData?.vehicleId ?? "",
      date: apiData?.date,
      expenseType: apiData?.expenseType ?? "",
      totalAmount: toNumOrUndef(apiData?.totalAmount) ?? 0,
      description: apiData?.description ?? ""
    };
  };

  const handleApiResponse = (apiResponse: any) => {
    const formValues = mapApiToForm(apiResponse);
    reset(formValues);
  };

  useEffect(() => {
    setValue("date", maxDate);
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId]);

  const fetchVehicleDatas = useCallback(async () => {
    try {
      const response = await ExpenseService.getvehicles(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        setVehicles(response.data.data);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const fetchfleetdatas = useCallback(async () => {
    try {
      const response = await FleetService.getfleetdatas(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        const list = response.data.data?.["otherExpenseTypes"] ?? [];
        setExpenseTypes(list);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const getmiscellaneousdata = useCallback(async () => {
    const requestdata = {
      expenseType: 3,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        let responsedata = response?.data?.data;
        let responsemaintancedata = responsedata.miscExpense;
        setAttachments(
          Array.isArray(responsemaintancedata?.documents)
            ? responsemaintancedata.documents
            : []
        );
        let maintancedata: any = {
          vehicleId: responsemaintancedata?.vehicleId,
          date: responsemaintancedata?.date,
          totalAmount: responsemaintancedata?.totalAmount,
          description: responsemaintancedata?.description,
          expenseType: responsemaintancedata?.expenseId,
          document: responsemaintancedata.document,
          id: data?.id
        };
        handleApiResponse(maintancedata);
        setdefaultformdata(maintancedata);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId, data?.id]);

  useEffect(() => {
    if (!loginUserId) return;
    (async () => {
      try {
        await fetchVehicleDatas();
        await fetchfleetdatas();
        if (data?.id) {
          await getmiscellaneousdata();
        }
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    })();
  }, [loginUserId, fetchVehicleDatas, fetchfleetdatas]);

  const onDateChange = (name: any, event: any) => {
    clearErrors(name);
    setValue(name, event);
  };

  const onFormSubmit = useCallback(
    async (form: FormMiscellaneous) => {
      const payload = {
        ...form,
        id: data?.id ? String(data.id) : "",
        attachments
      };
      try {
        const result = onSubmit(payload);
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    },
    [attachments, onSubmit]
  );

  const onCancel = useCallback(() => {
    setAttachments(
      Array.isArray(defaultformdata?.documents) ? defaultformdata.documents : []
    );
  }, [reset, defaultformdata?.documents]);

  return (
    <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
      <div className="mb-5 flex-row font-bold">
        <NeoHeader message="Miscellaneous Expense" />
      </div>

      <form
        key={data?.id ?? "new"}
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
      >
        {/* Row 1 */}
        <div className="mb-4 grid grid-cols-2 gap-2 max-sm:grid-cols-1">
          {/* Vehicle */}
          <Controller
            name="vehicleId"
            control={control}
            render={({field, fieldState}) => {
              const selectedOpt =
                vehicles.find(
                  (v: any) => String(v.id) === String(field.value)
                ) ?? null;
              return (
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="vehicleId"
                    className={`font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                  >
                    Vehicle
                  </label>
                  <Select
                    inputId="vehicleId"
                    value={selectedOpt}
                    onChange={(opt: any) => {
                      field.onChange(opt ? String(opt.id) : "");
                      clearErrors("vehicleId");
                    }}
                    options={vehicles}
                    getOptionLabel={(e: any) => e.regNo}
                    getOptionValue={(e: any) => String(e.id)}
                    placeholder="Select Vehicle"
                    styles={{
                      control: base => ({
                        ...base,
                        minHeight: "43px",
                        borderWidth: "2px",
                        borderColor: fieldState.invalid ? "#f31260" : "#d4d4d8",
                        borderRadius: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        boxShadow: "none",
                        fontSize: "14px",
                        backgroundColor: "white"
                      }),
                      indicatorSeparator: () => ({display: "none"}),
                      menu: base => ({
                        ...base,
                        borderRadius: "10px",
                        zIndex: 50
                      })
                    }}
                  />
                  {fieldState.error && (
                    <p className="text-xs text-[#f31260]" id="vehicleId-error">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              );
            }}
          />

          {/* Date */}
          <Controller
            name="date"
            control={control}
            render={({field: {ref, value, onChange, ...fieldRest}}) => (
              <NeoDatePicker
                {...fieldRest}
                label="Date"
                placeholder="Please select from date"
                errorMessage={errors.date && `${errors.date.message}`}
                maxDate={maxDate}
                value={value}
                handleDateChange={(e: any) => {
                  onChange(e);
                  onDateChange("date", e);
                }}
              />
            )}
          />
        </div>

        {/* Row 2 */}
        <div className="mb-4 grid grid-cols-2 gap-2 max-sm:grid-cols-1">
          {/* Expense Types */}
          <Controller
            name="expenseType"
            control={control}
            render={({field, fieldState}) => {
              const selectedOpt =
                expensetypes.find(
                  (v: any) => String(v.id) === String(field.value)
                ) ?? null;
              return (
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="expenseType"
                    className={`font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                  >
                    Expense Types
                  </label>
                  <Select
                    inputId="expenseType"
                    value={selectedOpt}
                    onChange={(opt: any) => {
                      field.onChange(opt ? String(opt.id) : "");
                      clearErrors("expenseType");
                    }}
                    options={expensetypes}
                    getOptionLabel={(e: any) => e.name}
                    getOptionValue={(e: any) => String(e.id)}
                    placeholder="Select expense types"
                    styles={{
                      control: base => ({
                        ...base,
                        minHeight: "43px",
                        borderWidth: "2px",
                        borderColor: fieldState.invalid ? "#f31260" : "#d4d4d8",
                        borderRadius: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        boxShadow: "none",
                        fontSize: "14px",
                        backgroundColor: "white"
                      }),
                      indicatorSeparator: () => ({display: "none"}),
                      menu: base => ({
                        ...base,
                        borderRadius: "10px",
                        zIndex: 50
                      })
                    }}
                  />
                  {fieldState.error && (
                    <p className="text-xs text-[#f31260]" id="vehicleId-error">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              );
            }}
          />

          {/* Total Amount */}
          <Controller
            name="totalAmount"
            control={control}
            render={({field}) => (
              <NeoTextBox
                label="Amount"
                id="totalAmount"
                placeholder="0.00"
                color={errors.totalAmount ? "danger" : "default"}
                isInvalid={!!errors.totalAmount}
                errorMessage={errors.totalAmount?.message as string}
                {...field}
                value={
                  field.value !== undefined && field.value !== null
                    ? String(field.value)
                    : ""
                }
              />
            )}
          />
        </div>

        {/* Row 3 */}
        <div className="mb-4 grid grid-cols-1 gap-2 max-sm:grid-cols-1">
          {/* Note */}
          <label htmlFor="Describe">Describe</label>
          <Controller
            name="description"
            control={control}
            render={({field}) => (
              <>
                <textarea
                  {...field}
                  value={field.value ?? ""}
                  className="w-full rounded-[17px] border-2 p-3 shadow-none hover:border-gray-600 focus:outline-none focus:ring-[#888888]"
                  placeholder="Describe the  Expense"
                  rows={4}
                  id="description"
                  autoComplete="off"
                  {...register("description")}
                />
                {typeof errors.description?.message === "string" && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.description.message}
                  </p>
                )}
              </>
            )}
          />
        </div>

        {/* Attachments */}
        {!data?.id && (
          <>
            <div className="mb-4 grid grid-cols-1">
              <UploadFormField
                value={attachments}
                onChangeFile={setAttachments}
                max={3}
                onError={msg => console.error(msg)}
              />
            </div>
            <hr />
          </>
        )}
        {/* Actions */}
        <div className="flex items-center justify-end gap-2 p-4">
          <NeoButton type="button" color="white" onClick={onCancel}>
            Cancel
          </NeoButton>
          <NeoButton
            type="submit"
            disabled={isSubmitting || loading}
            isLoading={isSubmitting || loading}
            spinner={
              <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
            }
            color="black"
          >
            Save
          </NeoButton>
        </div>
      </form>
    </div>
  );
};
